import "../styles/alert-details.scss";
import { useEffect, useState, useMemo, useRef, useCallback, memo } from "react";
import { useSelector } from "react-redux";
import * as DOMPurify from "dompurify";
import actions from "../actions/actions";
import { dispatch } from "../redux/redux-config";
import {
  AlertStatus,
  AlertType,
  DISCARD_ROUTES,
  Path,
  isMobile,
  MAX_DISPLAY,
} from "../utilities/constants";
import CircularProgress from "../actions/CircularProgress";
import { ConfirmDeleteModal } from "../components/confirm-delete-modal";
import { useUpdateState } from "../utilities/utilities";
import { showToast } from "../components/toast";
import LoadingSpinner from "../components/loading-spinner";
import navigate, { handleGoBack } from "../utilities/navigation";
import Analytic from "./Analytic";
import AudioPlayer from "../components/audio-player"; // Import the AudioPlayer component

export default function AlertDetails() {
  let alert = useSelector((store) => store.alert.current);
  const querySearch = window.location.search;
  let [state, updateState] = useUpdateState({});
  const [activeTab, setActiveTab] = useState("message");
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [showAllBody, setShowAllBody] = useState(false);
  const [showAllInbound, setShowAllInbound] = useState(false); // expand/collapse inbound replies
  const bodyRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [audioUrls, setAudioUrls] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [showAllMembers, setShowAllMembers] = useState(false);

  const groups = useMemo(() => alert?.groups ?? [], [alert]);
  const { regularGroups, tempGroups } = useMemo(() => {
    const regular = [];
    const temp = [];
    for (const g of groups) {
      if (g?.is_temp === 1) temp.push(g);
      else regular.push(g);
    }
    return { regularGroups: regular, tempGroups: temp };
  }, [groups]);

  useEffect(() => {
    let params = new URLSearchParams(querySearch);
    let id = parseInt(params.get("id")) || 0;
    if (id > 0) {
      actions.alerts.messagePreview(id);
    } else {
      // Invalid / missing id: redirect user back to Messages screen
      navigate(Path.Messages);
    }
  }, [querySearch]);

  async function convertTTS(description) {
    const result = await actions.alerts.textToSpeech(description);
    return result;
  }

  // convertVoice deprecated by direct convertTTS usage in effect

  // Track mounted state to avoid setState on unmounted component during async
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (alert?.alertType === AlertType.Voice) {
      if (
        alert?.voiceFileIdentifier &&
        alert.voiceFileIdentifier !== "Pending"
      ) {
        setAudioUrls(alert?.voiceFileUrl);
      } else if (alert?.message) {
        (async () => {
          try {
            const response = await convertTTS(alert.message);
            if (response?.success && isMountedRef.current) {
              setAudioUrls(response?.fileUrl);
            }
          } catch (err) {
            console.error("Error during TTS conversion:", err);
          }
        })();
      }
    }
  }, [
    alert?.alertType,
    alert?.voiceFileIdentifier,
    alert?.voiceFileUrl,
    alert?.message,
  ]);

  const fetchTempGroupMembers = async (id) => {
    try {
      const details = await actions.groups.getAllGroupMembers(id);
      if (Array.isArray(details?.members)) {
        return details.members;
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
    }
    return [];
  };

  useEffect(() => {
    let isActive = true;

    const loadTempGroupMembers = async () => {
      if (!tempGroups.length) {
        if (isActive) {
          setGroupMembers([]);
          setShowAllMembers(false);
        }
        return;
      }

      const ids = Array.from(
        new Set(
          tempGroups
            .map((group) => group?.id ?? group?.groupId)
            .filter(Boolean),
        ),
      );

      if (!ids.length) {
        if (isActive) {
          setGroupMembers([]);
          setShowAllMembers(false);
        }
        return;
      }

      const memberLists = await Promise.all(ids.map(fetchTempGroupMembers));

      if (!isActive) return;

      const deduped = [];
      const seen = new Set();

      for (const list of memberLists) {
        if (!Array.isArray(list)) continue;
        for (const member of list) {
          const key =
            member?.userId ??
            `${member?.firstName || ""}-${member?.lastName || ""}-${member?.id ?? ""}`;
          if (!key || seen.has(key)) continue;
          seen.add(key);
          deduped.push(member);
        }
      }
      setGroupMembers(deduped);
      setShowAllMembers(false);
    };

    loadTempGroupMembers();

    return () => {
      isActive = false;
    };
  }, [tempGroups]);

  // Derived status label (maps API numeric status to display text)
  const alertStatus = alert?.status;
  const statusLabel = useMemo(() => {
    switch (alertStatus) {
      case AlertStatus.Draft:
        return "Draft";
      case AlertStatus.Scheduled:
        return "Scheduled";
      case AlertStatus.Sent:
        return "Sent";
      case AlertStatus.Error:
        return "Failed";
      default:
        return "Message";
    }
  }, [alertStatus]);

  // Status icon class (icons aligned to spec intent)
  const statusIconClass = useMemo(() => {
    switch (alertStatus) {
      case AlertStatus.Draft:
        return "fa fa-edit";
      case AlertStatus.Scheduled:
        return "fa fa-clock";
      case AlertStatus.Sent:
        return "fa fa-paper-plane";
      case AlertStatus.Error:
        return "fa fa-exclamation-circle"; // Failed
      default:
        return "fa fa-info-circle";
    }
  }, [alertStatus]);

  // Support multi-channel badges if message was sent via >1 channel
  const channelBadges = useMemo(() => {
    const badges = [];
    // Prefer explicit include* flags if present (multi-channel scenario)
    const hasAnyExplicit =
      alert.includeText ||
      alert.includeEmail ||
      alert.includeVoice ||
      alert.includeVoiceIVR;
    if (hasAnyExplicit) {
      if (alert.includeText)
        badges.push({ key: "sms", label: "SMS", icon: "fa fa-comment-dots" });
      if (alert.includeEmail)
        badges.push({ key: "email", label: "Email", icon: "fa fa-envelope" });
      if (alert.includeVoice || alert.includeVoiceIVR)
        badges.push({ key: "voice", label: "Voice", icon: "fa fa-volume-up" });
    } else {
      // Fallback to single alertType
      switch (alert.alertType) {
        case AlertType.Text:
          badges.push({ key: "sms", label: "SMS", icon: "fa fa-comment-dots" });
          break;
        case AlertType.Email:
          badges.push({ key: "email", label: "Email", icon: "fa fa-envelope" });
          break;
        case AlertType.Voice:
          badges.push({
            key: "voice",
            label: "Voice",
            icon: "fa fa-volume-up",
          });
          break;
        default:
          badges.push({
            key: "message",
            label: "Message",
            icon: "fa fa-comment",
          });
          break;
      }
    }
    return badges;
  }, [
    alert.alertType,
    alert.includeText,
    alert.includeEmail,
    alert.includeVoice,
    alert.includeVoiceIVR,
  ]);

  // Single channel icon class retained for chip usage (first badge)
  const channelIconClass = useMemo(
    () => channelBadges[0]?.icon || "fa fa-comment",
    [channelBadges],
  );

  // Combined channel label string for accessibility (e.g., "SMS, Email")
  const channelLabel = useMemo(() => {
    return channelBadges.length
      ? channelBadges.map((cb) => cb.label).join(", ")
      : "Message";
  }, [channelBadges]);

  // Group chip logic
  const regularGroupList = regularGroups;
  const groupChips = useMemo(
    () =>
      showAllGroups ? regularGroupList : regularGroupList.slice(0, MAX_DISPLAY),
    [showAllGroups, regularGroupList],
  );
  const extraGroupCount = useMemo(() => {
    return regularGroupList.length > MAX_DISPLAY
      ? regularGroupList.length - MAX_DISPLAY
      : 0;
  }, [regularGroupList]);

  const { visibleMembers, extraMembers, extraMembersTitle } = useMemo(() => {
    if (showAllMembers) {
      return {
        visibleMembers: groupMembers,
        extraMembers: [],
        extraMembersTitle: "",
      };
    }
    const visible = groupMembers.slice(0, MAX_DISPLAY);
    const extras = groupMembers.slice(MAX_DISPLAY);
    const title = extras
      .map((m) => [m?.firstName, m?.lastName].filter(Boolean).join(" "))
      .filter(Boolean)
      .join(", ");
    return {
      visibleMembers: visible,
      extraMembers: extras,
      extraMembersTitle: title,
    };
  }, [groupMembers, showAllMembers]);

  // Message content sanitization + fallback
  const sanitizedHtml = useMemo(
    () => DOMPurify.sanitize(alert.message || ""),
    [alert.message],
  );
  const plainTextFallback = useMemo(() => {
    if (!alert.message) {
      if (alert.alertType === AlertType.Voice && alert.voiceFileUrl)
        return "(Voice recording message)";
      return "(No message content)";
    }
    return alert.message.replace(/<[^>]+>/g, "").trim();
  }, [alert.message, alert.alertType, alert.voiceFileUrl]);

  // Truncation measure
  useEffect(() => {
    if (bodyRef.current) {
      const height = bodyRef.current.offsetHeight;
      setIsTruncated(height > 1000);
    }
  }, [sanitizedHtml, activeTab]);

  const inboundMessages = useMemo(() => {
    const list = alert.inboundList || [];
    return list
      .slice()
      .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
  }, [alert.inboundList]);
  // On mobile: lazy-load inbound list via IntersectionObserver; desktop supports toggle
  const isMobileDevice = isMobile();
  const MOBILE_CHUNK = 10;
  const [mobileVisibleCount, setMobileVisibleCount] = useState(MOBILE_CHUNK);
  const mobileLoadMoreRef = useRef(null);

  useEffect(() => {
    if (!isMobileDevice) return;
    // Reset count when messages change or tab switches back to message
    setMobileVisibleCount(() => Math.min(MOBILE_CHUNK, inboundMessages.length));
  }, [isMobileDevice, inboundMessages.length, activeTab]);

  useEffect(() => {
    if (!isMobileDevice) return;
    if (activeTab !== "message") return;
    if (mobileVisibleCount >= inboundMessages.length) return;

    const el = mobileLoadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setMobileVisibleCount((prev) =>
              Math.min(prev + MOBILE_CHUNK, inboundMessages.length),
            );
          }
        });
      },
      { root: null, rootMargin: "0px", threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobileDevice, activeTab, mobileVisibleCount, inboundMessages.length]);

  const inboundVisible = useMemo(() => {
    if (isMobileDevice) return inboundMessages.slice(0, mobileVisibleCount);
    return showAllInbound ? inboundMessages : inboundMessages.slice(0, 3);
  }, [isMobileDevice, showAllInbound, inboundMessages, mobileVisibleCount]);

  const messageCreatedRaw = alert.createdDate || null;
  const messageCreatedDisplay = formatInboundTimestamp(messageCreatedRaw);

  function hasAvatar(url) {
    if (!url) return false;
    const cleaned = String(url).trim();
    return !!cleaned;
  }

  // Date formatting helper (convert ISO to 'Today at hh:mmam' or locale date)
  function formatInboundTimestamp(ts) {
    if (!ts) return "";
    try {
      const d = new Date(ts);
      const now = new Date();
      const isToday = d.toDateString() === now.toDateString();
      let hours = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12 || 12;
      const timePart = `${hours}:${minutes}${ampm}`;
      if (isToday) return `Today at ${timePart}`;
      return (
        d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
        ` ${timePart}`
      );
    } catch (e) {
      return ts;
    }
  }

  const back = useCallback(() => {
    handleGoBack();
  }, []);

  const edit = useCallback(() => {
    if (!alert?.id) return;
    actions.alerts.loadAlertDetails(alert.id);
    actions.alerts.showEditAlert(alert, DISCARD_ROUTES.messages);
  }, [alert]);

  const onDelete = useCallback(async () => {
    if (!alert?.id) return;
    updateState({ deletingDraft: true });
    const result = await actions.alerts.deleteAlert(alert.id);
    updateState({
      showConfirmDelete: false,
      deleteConfirmClose: false,
      deletingDraft: false,
    });
    if (result?.success) {
      dispatch("ALERT_SAVING", false);
      dispatch("RESET_MY_ALERTS");
      navigate(Path.Messages);
      showToast("removed", "Your Alert has been removed.");
    } else {
      showToast("error", "Unable to delete the alert. Please try again.");
    }
  }, [alert?.id, updateState]);

  // Helper for group chips (design: first 5 then +X more)
  const GroupChips = memo(function GroupChipsComponent({
    chips,
    showAll,
    extraCount,
    onShowAll,
    onShowLess,
    channelIcon,
    channelLabelText,
  }) {
    return (
      <div className="group-chips" aria-label="Recipient groups">
        {chips.map((g, index) => {
          const groupName = g?.name || "Group";
          return (
            <span
              className="group-chip"
              key={g?.id ?? g?.groupId ?? index}
              tabIndex={0}
              aria-label={`Group ${groupName} (${channelLabelText})`}
            >
              <i
                className={`${channelIcon} group-channel-icon`}
                aria-hidden="true"
              ></i>
              <span className="group-chip-name">{groupName}</span>
              <i
                className="fa fa-users group-users-icon"
                aria-hidden="true"
              ></i>
            </span>
          );
        })}
        {!showAll && extraCount > 0 && (
          <button
            type="button"
            className="group-chip more-chip"
            aria-label={`Show ${extraCount} more groups`}
            onClick={onShowAll}
          >
            +{extraCount} more
          </button>
        )}
        {showAll && extraCount > 0 && (
          <button
            type="button"
            className="group-chip less-chip"
            aria-label="Show fewer groups"
            onClick={onShowLess}
          >
            Show less
          </button>
        )}
      </div>
    );
  });

  const IndividualChips = memo(function IndividualChipsComponent({
    visible,
    extras,
    extrasTitle,
    showAll,
    totalCount,
    onShowAll,
    onShowLess,
  }) {
    return (
      <div className="group-chips" aria-label="Recipient individuals">
        {visible.map((member, index) => {
          const name = [member?.firstName, member?.lastName]
            .filter(Boolean)
            .join(" ");
          const key = member?.userId ?? `${name}-${index}`;
          return (
            <span
              className="group-chip individual-chip"
              key={key}
              tabIndex={0}
              aria-label={name ? `Individual ${name}` : "Recipient"}
            >
              <i
                className="fa fa-user group-channel-icon"
                aria-hidden="true"
              ></i>
              <span className="group-chip-name">{name || "Recipient"}</span>
            </span>
          );
        })}
        {extras.length > 0 && !showAll && (
          <button
            type="button"
            className="group-chip more-chip"
            title={extrasTitle || undefined}
            aria-label={`Show ${extras.length} more individuals`}
            onClick={onShowAll}
          >
            +{extras.length} more
          </button>
        )}
        {showAll && totalCount > MAX_DISPLAY && (
          <button
            type="button"
            className="group-chip less-chip"
            aria-label="Show fewer individuals"
            onClick={onShowLess}
          >
            Show less
          </button>
        )}
      </div>
    );
  });

  const actionMenuRef = useRef(null);
  useEffect(() => {
    if (!state.showActionMenu) return;
    const handleDocumentClick = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        updateState({ showActionMenu: false });
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") {
        updateState({ showActionMenu: false });
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const items =
          actionMenuRef.current?.querySelectorAll(".ad-action-item");
        if (!items || items.length === 0) return;
        e.preventDefault();
        const arr = Array.from(items);
        const currentIndex = arr.findIndex(
          (el) => el === document.activeElement,
        );
        let nextIndex;
        if (currentIndex === -1) {
          nextIndex = e.key === "ArrowDown" ? 0 : arr.length - 1;
        } else {
          nextIndex =
            e.key === "ArrowDown"
              ? (currentIndex + 1) % arr.length
              : (currentIndex - 1 + arr.length) % arr.length;
        }
        arr[nextIndex].focus();
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [state.showActionMenu, updateState]);

  return (
    <div className="alert-details redesigned">
      {/* Header Section */}
      <div className="ad-header" style={{ position: "relative" }}>
        <span
          id="back"
          className="nav-button"
          onClick={back}
          aria-label="Back to Messages"
        >
          <i className="far fa-chevron-left" aria-hidden="true"></i>
          <span className="nav-button-text">Messages</span>
        </span>
        <div className="ad-title-block" aria-label="Message header">
          <h2
            className="ad-title"
            title={alert.name || "Message"}
            aria-label={alert.name || "Message"}
          >
            {alert.name || "Message"}
          </h2>
          <div className="ad-tags" aria-label="Message meta">
            <span
              className={`ad-tag status-${statusLabel.toLowerCase()}`}
              aria-label={`Status: ${statusLabel}`}
            >
              <i className={statusIconClass} aria-hidden="true"></i>
              <span className="ad-tag-text">{statusLabel}</span>
            </span>
            {channelBadges.map((cb) => (
              <span
                key={cb.key}
                className={`ad-tag channel-${cb.key}`}
                aria-label={`Channel: ${cb.label}`}
              >
                <i className={cb.icon} aria-hidden="true"></i>
                <span className="ad-tag-text">{cb.label}</span>
              </span>
            ))}
          </div>

          <div
            className={`ad-action-menu inline${state.showActionMenu ? " open" : ""}`}
            ref={actionMenuRef}
          >
            <button
              type="button"
              className="ad-action-trigger"
              aria-haspopup="true"
              aria-expanded={state.showActionMenu ? "true" : "false"}
              aria-label="Message actions"
              onClick={() =>
                updateState({ showActionMenu: !state.showActionMenu })
              }
              onKeyDown={(e) => {
                if (e.key === "Escape") updateState({ showActionMenu: false });
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  const first =
                    actionMenuRef.current?.querySelector(".ad-action-item");
                  first && first.focus();
                }
              }}
            >
              <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
            </button>
            {state.showActionMenu && (
              <ul
                className="ad-action-dropdown"
                role="menu"
                aria-label="Message action menu"
              >
                {alert.status === AlertStatus.Sent && (
                  <li
                    role="menuitem"
                    tabIndex={0}
                    className="ad-action-item"
                    onClick={() => {
                      updateState({ showActionMenu: false });
                      actions.alerts.copyAsDraft(alert);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        actions.alerts.copyAsDraft(alert);
                        updateState({ showActionMenu: false });
                      }
                    }}
                    aria-label="Copy message as draft"
                  >
                    <i className="fa fa-copy" aria-hidden="true"></i> Copy as
                    Draft
                  </li>
                )}
                {alert.status === AlertStatus.Sent && (
                  <li
                    role="menuitem"
                    tabIndex={0}
                    className="ad-action-item"
                    onClick={() => {
                      updateState({ showActionMenu: false });
                      setActiveTab("analytics");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActiveTab("analytics");
                        updateState({ showActionMenu: false });
                      }
                    }}
                    aria-label="View Details"
                  >
                    <i className="fa fa-chart-bar" aria-hidden="true"></i> View
                    Details
                  </li>
                )}
                {(alert.status === AlertStatus.Draft ||
                  alert.status === AlertStatus.Scheduled) && (
                  <>
                    <li
                      role="menuitem"
                      tabIndex={0}
                      className="ad-action-item"
                      onClick={() => {
                        updateState({ showActionMenu: false });
                        edit();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          edit();
                          updateState({ showActionMenu: false });
                        }
                      }}
                      aria-label="Edit message"
                    >
                      <i className="fa fa-edit" aria-hidden="true"></i> Edit
                    </li>
                    <li
                      role="menuitem"
                      tabIndex={0}
                      className="ad-action-item"
                      onClick={() => {
                        updateState({
                          showActionMenu: false,
                          deleteConfirmClose: true,
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          updateState({
                            deleteConfirmClose: true,
                            showActionMenu: false,
                          });
                        }
                      }}
                      aria-label="Delete message"
                    >
                      <i className="fa fa-trash" aria-hidden="true"></i> Delete
                    </li>
                  </>
                )}
                {alert.status === AlertStatus.Error && (
                  <li
                    role="menuitem"
                    tabIndex={0}
                    className="ad-action-item"
                    onClick={() => {
                      updateState({ showActionMenu: false });
                      setActiveTab("analytics");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActiveTab("analytics");
                        updateState({ showActionMenu: false });
                      }
                    }}
                    aria-label="View Details"
                  >
                    <i className="fa fa-chart-bar" aria-hidden="true"></i> View
                    Details
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
        <div className="ad-tabs">
          <span
            className={`ad-tab${activeTab === "message" ? " active" : ""}`}
            onClick={() => setActiveTab("message")}
          >
            Message
          </span>
          {alert?.status === 1 && (
            <span
              className={`ad-tab${activeTab === "analytics" ? " active" : ""}`}
              onClick={() => setActiveTab("analytics")}
            >
              Analytics
            </span>
          )}
        </div>
      </div>
      {/* Message Card Section */}
      {activeTab === "message" && (
        <div className="ad-message-card" style={{ position: "relative" }}>
          <div className="ad-message-header">
            <div className="ad-header-row">
              <div className="ad-user-info">
                {hasAvatar(alert.profilePhotoURL) ? (
                  <img
                    className="ad-avatar outlined"
                    src={alert.profilePhotoURL}
                    alt={alert?.senderName || "Sender"}
                  />
                ) : (
                  <div
                    className="ad-avatar ad-avatar-fallback outlined"
                    aria-label="Default sender avatar"
                  >
                    <i className="fa fa-user" aria-hidden="true"></i>
                  </div>
                )}
                <span className="ad-user-name">
                  {alert?.senderName || "Sender"}
                </span>
              </div>
              {messageCreatedRaw && (
                <div
                  className="ad-message-time-right"
                  title={messageCreatedRaw}
                  aria-label={`Message created ${messageCreatedDisplay}`}
                >
                  {messageCreatedDisplay}
                </div>
              )}
            </div>
            {regularGroupList.length > 0 && (
              <div className="ad-message-groups pad-left-avatar">
                <span className="ad-sent-label">Sent to</span>
                <GroupChips
                  chips={groupChips}
                  showAll={showAllGroups}
                  extraCount={extraGroupCount}
                  onShowAll={() => setShowAllGroups(true)}
                  onShowLess={() => setShowAllGroups(false)}
                  channelIcon={channelIconClass}
                  channelLabelText={channelLabel}
                />
              </div>
            )}
            {groupMembers.length > 0 && (
              <div className="ad-message-groups pad-left-avatar">
                <span className="ad-sent-label">Individuals</span>
                <IndividualChips
                  visible={visibleMembers}
                  extras={extraMembers}
                  extrasTitle={extraMembersTitle}
                  showAll={showAllMembers}
                  totalCount={groupMembers.length}
                  onShowAll={() => setShowAllMembers(true)}
                  onShowLess={() => setShowAllMembers(false)}
                />
              </div>
            )}
          </div>
          <div
            className="ad-message-body"
            aria-label="Message body"
            style={
              isMobile()
                ? { overflow: "auto", maxWidth: "100vw", maxHeight: "60vh" }
                : {}
            }
          >
            <div className="ad-message-content pad-left-avatar">
              {alert.alertType !== AlertType.Voice && (
                <div className="ad-message-text">
                  {sanitizedHtml ? (
                    <div
                      ref={bodyRef}
                      className={`ad-body-html${
                        isTruncated && !showAllBody ? " truncated" : ""
                      }`}
                      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                    />
                  ) : (
                    <div className="ad-body-fallback">{plainTextFallback}</div>
                  )}
                  {isTruncated && (
                    <button
                      type="button"
                      className="ad-show-toggle"
                      aria-label={
                        showAllBody ? "Show less content" : "Show full content"
                      }
                      onClick={() => setShowAllBody(!showAllBody)}
                    >
                      {showAllBody ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              )}

              {/* Optionally add attachments, voice, etc. here */}
              {alert.alertType === AlertType.Voice && (
                <div>
                  <label className="voice-label">Sound File</label>
                  {alert.voiceFileUrl && (
                    <div className="voice-preview">
                      {audioUrls ? (
                        <AudioPlayer src={audioUrls} autoPlay={false} />
                      ) : (
                        <CircularProgress />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* <div className="ad-message-footer">
            <img
              className="ad-footer-avatar"
              src="https://randomuser.me/api/portraits/women/45.jpg"
              alt="Reply"
            />
            <span className="ad-footer-text">
              If you have any questions or need further details, feel free to
              reach out.
            </span>
          </div> */}
          </div>
        </div>
      )}
      {activeTab === "analytics" && alert?.status === 1 && (
        <>
          <Analytic alert={alert} />
        </>
      )}
      {/* Count & inbound replies only for non-email alerts */}
      {activeTab === "message" && alert.alertType !== AlertType.Email && (
        <>
          {/* Count circle and horizontal line (desktop only) */}
          {!isMobileDevice && (
            <div className="ad-count-section-static">
              <div
                className={`ad-count-circle-static${
                  showAllInbound ? " expanded" : ""
                }`}
                role="button"
                tabIndex={0}
                aria-label={
                  showAllInbound
                    ? "Collapse inbound replies"
                    : `Show all ${inboundMessages.length} replies`
                }
                onClick={() => setShowAllInbound((s) => !s)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowAllInbound((s) => !s);
                  }
                }}
              >
                {inboundMessages.length}
              </div>
              <div className="ad-count-divider-static"></div>
            </div>
          )}
          {/* Comments Section */}
          <div className="ad-comments-section">
            <div
              className={
                isMobileDevice
                  ? "ad-comments-list"
                  : `ad-comments-list${showAllInbound ? " scroll" : ""}`
              }
              aria-label="Replies list"
            >
              {inboundMessages.length === 0 && (
                <div
                  className="ad-comment-empty"
                  aria-label="No inbound replies"
                >
                  No inbound replies yet.
                </div>
              )}
              {inboundVisible.map((m, idx) => (
                <div className="ad-comment" key={idx + m.createdDate}>
                  {hasAvatar(m.profilePhotoURL) ? (
                    <img
                      className="ad-comment-avatar"
                      src={m.profilePhotoURL}
                      alt={m.userName}
                    />
                  ) : (
                    <div
                      className="ad-comment-avatar ad-comment-avatar-fallback"
                      aria-label={`Default avatar for ${m.userName}`}
                    >
                      <i className="fa fa-user" aria-hidden="true"></i>
                    </div>
                  )}
                  <div className="ad-comment-content">
                    <span className="ad-comment-name">{m.userName}</span>
                    <span className="ad-comment-text">{m.message}</span>
                  </div>
                  <span className="ad-comment-time">
                    {formatInboundTimestamp(m.createdDate)}
                  </span>
                </div>
              ))}
              {isMobileDevice &&
                mobileVisibleCount < inboundMessages.length && (
                  <div
                    className="ad-mobile-load-more"
                    ref={mobileLoadMoreRef}
                    aria-label="Load more replies"
                  >
                    Loading more...
                  </div>
                )}
              {!isMobileDevice &&
                !showAllInbound &&
                inboundMessages.length > 3 && (
                  <div
                    className="ad-inline-more-hint"
                    aria-label="Showing first 3 replies"
                  >
                    Showing 3 of {inboundMessages.length}. Click the count to
                    view all.
                  </div>
                )}
            </div>
          </div>
          {/* Delete button commented out per design (retain code for future use) */}
          {/**
          <div className="btn-container">
            {canDelete && (
              <GuiButton
                dataQa="delete"
                className="btn-outline"
                onClick={(e) => updateState({ deleteConfirmClose: true })}
              >
                Delete
              </GuiButton>
            )}
          </div>
          **/}
        </>
      )}
      {state.deleteConfirmClose && (
        <ConfirmDeleteModal
          status="remove"
          onDelete={onDelete}
          onCancel={(e) => updateState({ deleteConfirmClose: false })}
          deleting={state.deletingDraft}
        />
      )}
      <LoadingSpinner show={!alert.id} />
    </div>
  );
}
